import { waitForFlickity } from '@utilities/theme';

if (!customElements.get('slider-wrapper')) {
  class SliderWrapper extends HTMLElement {
    constructor() {
      super();
      this.breakpointMobile = 767;
      this.initialized = false;
      this.resizeTimeout = null;
      this.onResize = this.onResize.bind(this);
      this.waitForFlickityCleanup = null;
    }

    allowDebug(){
      return this.classList.contains('debug');
    }

    // Selector for natively-focusable descendants we need to manage for hidden slides.
    get focusableSelector() {
      return 'a[href], button, input, select, textarea, [tabindex], iframe, [contenteditable]';
    }

    // Set tabindex="-1" on focusable descendants of a hidden cell (remembering the prior value),
    // or restore the previous tabindex when the cell becomes visible again.
    setCellFocusable(cell, focusable) {
      const elements = cell.querySelectorAll(this.focusableSelector);
      elements.forEach((el) => {
        if (focusable) {
          // restore: only act on elements we previously modified
          if (el.hasAttribute('data-prev-tabindex')) {
            const prev = el.getAttribute('data-prev-tabindex');
            if (prev === '') {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', prev);
            }
            el.removeAttribute('data-prev-tabindex');
          }
        } else {
          // hide: remember the original tabindex once, then make untabbable
          if (!el.hasAttribute('data-prev-tabindex')) {
            el.setAttribute('data-prev-tabindex', el.getAttribute('tabindex') ?? '');
          }
          el.setAttribute('tabindex', '-1');
        }
      });
    }

    // Keep hidden/off-screen cells out of the tab order. Flickity maintains aria-hidden on
    // off-screen cells (and supports showing multiple cells at once via groupCells/cellAlign),
    // so we treat any cell with aria-hidden="true" as hidden rather than matching selectedIndex.
    updateSlideTabIndexes() {
      if (!this.slider) return;
      const cells = this.slider.getCellElements ? this.slider.getCellElements() : this.slider.cells.map(c => c.element);
      cells.forEach((cell) => {
        const hidden = cell.getAttribute('aria-hidden') === 'true';
        this.setCellFocusable(cell, !hidden);
      });
    }

    calculateSliderOptionsIndex() {
      return window.innerWidth > this.breakpointMobile ? 0 : 1;
    }

    getCurrentOptions(){
      return this.sliderOptions[this.sliderOptionsIndex];
    }

    getOptionSets(){
      return this.sliderOptions;
    }

    processSliderOptions() {
      // clean out any empty data points
      this.sliderOptions.forEach(optionSet => {
        for (const property in optionSet) {
          if (optionSet[property] === '' || optionSet[property] === null) {
            delete optionSet[property];
          }
        }
      });
      if(this.allowDebug()){
        console.log('sliderOptions', this.sliderOptions);
      }
      // scrub the DOM of any settings data
      if(!this.allowDebug()){
        delete this.dataset.desktopSettings;
        delete this.dataset.mobileSettings;
      }
    }

        // Wait for images to load before initializing slider (only for specific components)
    async waitForImages() {
      // Only wait for images in filter image components or other components that specifically need it
      if (!this.needsImageWait()) {
        return;
      }

      const images = this.querySelectorAll('img');
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) {
          return Promise.resolve();
        }
        return new Promise((resolve) => {
          img.addEventListener('load', resolve);
          img.addEventListener('error', resolve); // Still resolve on error to avoid hanging
          // Timeout after 3 seconds to avoid hanging
          setTimeout(resolve, 3000);
        });
      });

      await Promise.all(imagePromises);
    }

    // Check if this slider needs to wait for images
    needsImageWait() {
      return this.classList.contains('filter__images-slider--inner') ||
             this.hasAttribute('data-wait-for-images');
    }

    async initSlider(isResize = false) {
      if (!window.Flickity) return;

      // grab the current option set
      const currentOptions = this.sliderOptions[this.sliderOptionsIndex];

      // if the slider is already initialized and we are not resizing, exit
      if (this.initialized && !isResize) return;

      // if we want this set disabled, destroy and exit
      if (!currentOptions.enabled) {
        if (this.slider) {
          this.slider.destroy();
          this.initialized = false;
        }
        this.classList.remove('is-initialized');
        return;
      }

      // if we are resizing, destroy the slider instance
      if (isResize && this.slider) {
        this.slider.destroy();
        this.initialized = false;
      }

      // Wait for images to load before initializing
      await this.waitForImages();

      // Remove opacity transition during initialization to prevent flash
      this.style.opacity = '1';

      // reinit the slider with a new flickity instance with the latest dataset
      try {
        this.slider = new window.Flickity(this, currentOptions);
        this.initialized = true;
        this.classList.add('is-initialized');

        // when the slider is settled, clean it up a little bit.
        this.slider.on('settle', () => {
          this.slider.reloadCells();
          this.slider.resize();

          if (this.hasAttribute('check-height')) {
            this.updateSliderHeight();
          }

          // keep off-screen cells out of the tab order once aria-hidden has settled
          this.updateSlideTabIndexes();
        });

        // on every slide change, refresh which cells' focusables are tabbable
        this.slider.on('select', () => {
          this.updateSlideTabIndexes();
        });

        // run once after init (aria-hidden is set by Flickity just after construction)
        this.updateSlideTabIndexes();

        // Handle lazy loading completion
        this.slider.on('lazyLoad', () => {
          // Small delay to allow image to fully render
          setTimeout(() => {
            this.slider.resize();
            if (this.hasAttribute('check-height')) {
              this.updateSliderHeight();
            }
          }, 100);
        });

      } catch (error) {
        console.error('Failed to initialize Flickity slider:', error);
      }
    }

    updateSliderHeight() {
      const viewport = this.querySelector('.flickity-viewport');
      const selected = this.querySelector('.is-selected');
      if (viewport && selected) {
        const newHeight = selected.offsetHeight;
        if (newHeight > 0) {
          viewport.style.height = `${newHeight}px`;
          // Trigger window resize to update other components
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            if (this.slider) {
              this.slider.resize();
            }
          }, 50);
        }
      }
    }

    initResizeEvent() {
      window.addEventListener('resize', this.onResize);
    }

    onResize() {
      // Clear previous timeout
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      // Debounce resize events
      this.resizeTimeout = setTimeout(() => {
        const newIndex = this.calculateSliderOptionsIndex();
        // if we have a new index aka options set, re-init the slider
        if (this.sliderOptionsIndex !== newIndex) {
          this.sliderOptionsIndex = newIndex;
          this.initSlider(true);
        } else if (this.slider) {
          // Just resize the existing slider
          this.slider.resize();
          if (this.hasAttribute('check-height')) {
            this.updateSliderHeight();
          }
        }
      }, 250);
    }

    startSlider() {
      // Initialize slider with minimal delay only for components that need it
      const initDelay = this.needsImageWait() ? 100 : 0;
      setTimeout(() => {
        this.initSlider(false);
      }, initDelay);

      this.initResizeEvent();
      this.initWheelEvent();

      // extra events if we are dealing with a navigation slider
      if (this.getAttribute('type') === 'nav') {
        this.initSliderButtons();
      }
    }

    initSliderButtons() {
      const currentOptions = this.sliderOptions[this.sliderOptionsIndex];
      const targetSlider = document.querySelector(currentOptions.asNavFor);
      if (!targetSlider) return;

      const prev = this.querySelector('.flickity-prev-next-button.previous');
      const next = this.querySelector('.flickity-prev-next-button.next');

      if (prev && next) {
        prev.addEventListener('click', () => targetSlider.slider?.previous());
        next.addEventListener('click', () => targetSlider.slider?.next());
      }
    }

    initWheelEvent() {
      if (this.wheelInitialized) return;
      this.wheelInitialized = true;

      // Wheel event listener to navigate the slider.

      // Trackpad protection: trackpads fire many wheel events per swipe, so accumulate deltaX and
      // advance one slide at a time instead of calling next/previous on every event.
      this.wheelAccumulatedDelta = 0;
      this.wheelResetTimeout = null;
      this.wheelLastAdvanceTime = 0;
      const THRESHOLD = 50; // Total horizontal delta before advancing one slide
      const COOLDOWN = 400; // Minimum ms between slide changes within a gesture
      const RESET_DELAY = 150; // Reset accumulator after gesture pauses

      this.handleWheel = (event) => {
        if (!window.Flickity) return;

        const flkty = window.Flickity.data(this);
        if (!flkty) return;

        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        // Only prevent default if we're actually going to handle the event
        event.preventDefault();

        const now = Date.now();
        if (now - this.wheelLastAdvanceTime < COOLDOWN) return;

        this.wheelAccumulatedDelta += event.deltaX;

        clearTimeout(this.wheelResetTimeout);
        this.wheelResetTimeout = setTimeout(() => {
          this.wheelAccumulatedDelta = 0;
        }, RESET_DELAY);

        if (Math.abs(this.wheelAccumulatedDelta) < THRESHOLD) return;

        if (this.wheelAccumulatedDelta > 0) {
          flkty.next();
        } else {
          flkty.previous();
        }

        this.wheelAccumulatedDelta = 0;
        this.wheelLastAdvanceTime = now;
      };

      this.addEventListener('wheel', this.handleWheel, { passive: false }); // Explicitly set passive to false since we need preventDefault
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);

      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }
      if (typeof this.waitForFlickityCleanup === 'function') {
        this.waitForFlickityCleanup();
        this.waitForFlickityCleanup = null;
      }

      if (this.handleWheel) {
        this.removeEventListener('wheel', this.handleWheel, { passive: false });
        this.handleWheel = null;
      }

      if (this.wheelResetTimeout) {
        clearTimeout(this.wheelResetTimeout);
        this.wheelResetTimeout = null;
      }

      this.wheelInitialized = false;
    }

    connectedCallback() {
      // if the component is disabled, cancel the component.
      if (this.hasAttribute('disabled')) return;

      // set up default values and option data
      this.sliderOptionsDesktop = JSON.parse(this.dataset.desktopSettings) || {};
      this.sliderOptionsMobile = JSON.parse(this.dataset.mobileSettings) || {};
      this.sliderOptions = [this.sliderOptionsDesktop, this.sliderOptionsMobile];
      this.sliderOptionsIndex = this.calculateSliderOptionsIndex();

      // process the options, init the slider, establish events
      this.processSliderOptions();
      if (typeof waitForFlickity === 'function') {
        this.waitForFlickityCleanup = waitForFlickity(() => this.startSlider(), {
          intervalMs: 250,
          maxAttempts: 80
        });
        return;
      }

      if (window.Flickity) {
        this.startSlider();
      }
    }

    // Public method to force re-initialization
    reinitialize() {
      this.initSlider(true);
    }
  }

  customElements.define('slider-wrapper', SliderWrapper);
}

